/**
 * PhotoSection Component
 * Collapsible section for adding photos to a check-in
 */

import { confirm } from '@/components/ui/confirmation-dialog';
import { toast } from '@/components/ui/toast';
import { useAppTheme } from '@/providers/theme-provider';
import {
  ImagePickerResult,
  pickImageFromGallery,
  showImagePickerOptions,
  takePhotoWithCamera,
} from '@/utils/image-picker';
import { validatePhotoBeforeUpload } from '@/utils/photo-validation';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { PhotoAsset } from '../types';

export interface PhotoSectionProps {
  /** Array of photo assets */
  photos: PhotoAsset[];
  /** Callback when a photo is added */
  onAdd: (photo: PhotoAsset) => void;
  /** Callback when a photo is removed */
  onRemove: (photoId: string) => void;
  /** Maximum number of photos allowed */
  maxPhotos?: number;
  /** Whether the section is disabled */
  disabled?: boolean;
}

/**
 * Generate a unique ID for a photo
 */
function generatePhotoId(): string {
  return `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function PhotoSection({
  photos,
  onAdd,
  onRemove,
  maxPhotos = 3,
  disabled = false,
}: PhotoSectionProps) {
  const theme = useAppTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isPickingImage, setIsPickingImage] = useState(false);

  const canAddMore = photos.length < maxPhotos;

  const handleImagePicked = useCallback(
    async (result: ImagePickerResult) => {
      if (!result.cancelled && result.uri) {
        const validation = await validatePhotoBeforeUpload(result.uri, result.type);
        if (!validation.valid) {
          toast.error(validation.error || 'Invalid photo file');
          setIsPickingImage(false);
          return;
        }

        const newPhoto: PhotoAsset = {
          id: generatePhotoId(),
          uri: result.uri,
          mimeType: result.type,
          file: result.file,
          isUploading: false,
        };
        onAdd(newPhoto);
        setIsExpanded(true);
      }
      setIsPickingImage(false);
    },
    [onAdd]
  );

  const handleCameraPress = useCallback(async () => {
    if (!canAddMore || disabled) return;

    setIsPickingImage(true);
    try {
      const result = await takePhotoWithCamera({
        allowsEditing: false,
        showAdvancedCrop: true,
        quality: 0.8,
      });
      await handleImagePicked(result);
    } catch (error) {
      setIsPickingImage(false);
      const message =
        error instanceof Error ? error.message : 'Failed to access camera';
      toast.error('Camera error', { description: message });
    }
  }, [canAddMore, disabled, handleImagePicked]);

  const handleGalleryPress = useCallback(async () => {
    if (!canAddMore || disabled) return;

    setIsPickingImage(true);
    try {
      const result = await pickImageFromGallery({
        allowsEditing: false,
        showAdvancedCrop: true,
        quality: 0.8,
      });
      await handleImagePicked(result);
    } catch (error) {
      setIsPickingImage(false);
      const message =
        error instanceof Error ? error.message : 'Failed to access gallery';
      toast.error('Gallery error', { description: message });
    }
  }, [canAddMore, disabled, handleImagePicked]);

  const handleAddPress = useCallback(() => {
    if (!canAddMore) {
      toast.warning('Maximum photos reached', {
        description: `You can only upload up to ${maxPhotos} photos.`,
      });
      return;
    }

    showImagePickerOptions(
      handleCameraPress,
      handleGalleryPress,
      () => setIsPickingImage(false)
    );
  }, [canAddMore, maxPhotos, handleCameraPress, handleGalleryPress]);

  const handleRemove = useCallback(
    async (photoId: string) => {
      const confirmed = await confirm({
        title: 'Remove this photo?',
        description: 'Are you sure you want to remove this photo?',
        variant: 'destructive',
        confirmLabel: 'Remove',
      });
      if (confirmed) {
        onRemove(photoId);
      }
    },
    [onRemove]
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <View className="mb-4">
      {/* Header Row */}
      <Pressable
        onPress={toggleExpanded}
        className="flex-row items-center justify-between pt-3"
        disabled={true}
      >
        <View className="flex-row flex-1 items-center">
          <Text className="text-xl text-foreground flex-1">
            Photos
          </Text>
        </View>
        <Text className="text-xs text-muted-foreground">
          {photos.length} of {maxPhotos}
        </Text>
      </Pressable>

      {/* Expanded Content */}
      {isExpanded && (
        <View className="mt-2">
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingRight: 8 }}
          >
            <View className="flex-row gap-2">
              {/* Existing Photos */}
              {photos.map((photo) => (
                <View
                  key={photo.id}
                  className="relative rounded-lg overflow-hidden bg-surface-secondary"
                  style={{ width: 88, height: 88 }}
                >
                  <Image
                    source={{ uri: photo.uri }}
                    className="w-full h-full"
                    resizeMode="cover"
                    accessibilityLabel={`Check-in photo ${photos.indexOf(photo) + 1}`}
                  />

                  {/* Upload Progress */}
                  {photo.isUploading && (
                    <View
                      className="absolute inset-0 items-center justify-center"
                      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    >
                      <ActivityIndicator size="small" color="#fff" />
                    </View>
                  )}

                  {/* Error Overlay */}
                  {photo.uploadError && (
                    <View
                      className="absolute inset-0 items-center justify-center"
                      style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)' }}
                    >
                      <Ionicons name="alert-circle" size={24} color="#fff" />
                    </View>
                  )}

                  {/* Remove Button */}
                  {!photo.isUploading && !disabled && (
                    <Pressable
                      onPress={() => handleRemove(photo.id)}
                      className="absolute top-1 right-1 rounded-full items-center justify-center"
                      style={{
                        width: 24,
                        height: 24,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                      }}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      accessibilityRole="button"
                      accessibilityLabel={`Remove photo ${photos.indexOf(photo) + 1}`}
                    >
                      <Ionicons name="close" size={14} color="#fff" />
                    </Pressable>
                  )}
                </View>
              ))}

              {/* Add Photo Button */}
              {canAddMore && !disabled && (
                <Pressable
                  onPress={handleAddPress}
                  disabled={isPickingImage}
                  className="rounded-lg items-center justify-center bg-muted border-2 border-dotted border- border-dim"
                  style={{ width: 88, height: 88 }}
                >
                  {isPickingImage ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <>
                      <Ionicons
                        name="add"
                        size={24}
                        className="text-muted-foreground"
                      />
                    </>
                  )}
                </Pressable>
              )}
            </View>
          </ScrollView>
        </View>
      )}
    </View>
  );
}

export default PhotoSection;
